import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { toPrimaryLegalRole } from "@/shared/utils/syncRoleColumns";

export type LegalRole = "trustee" | "executor" | "administrator" | "none";

export interface UserEstateRole {
  isNominated: boolean;
  isTrustee: boolean;
  isExecutor: boolean;
  isAdministrator: boolean;
  hasLegalAuthority: boolean;
  legalRole: LegalRole;
  relationshipToDecedent: string | null;
  isLoading: boolean;
  isFetching: boolean;
}

export function useUserEstateRole(): UserEstateRole {
  const { user, person } = useAppContext();
  const userId = user?.user_id;
  const estateId = person?.estate_id;

  const enabled = !!userId && !!estateId;

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["userEstateRole", userId, estateId],
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from("user_estates")
        .select("is_nominated, is_trustee, is_executor, is_administrator, relationship_to_decedent")
        .eq("user_id", userId!)
        .eq("estate_id", estateId!)
        .maybeSingle();

      if (error) throw error;
      return row;
    },
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return useMemo(() => {
    const isNominated = data?.is_nominated ?? false;
    const isTrustee = data?.is_trustee ?? false;
    const isExecutor = data?.is_executor ?? false;
    const isAdministrator = data?.is_administrator ?? false;
    const hasLegalAuthority = isTrustee || isExecutor || isAdministrator;
    const legalRole: LegalRole =
      toPrimaryLegalRole({
        is_nominated: isNominated,
        is_executor: isExecutor,
        is_administrator: isAdministrator,
        is_trustee: isTrustee,
      }) ?? "none";

    return {
      isNominated,
      isTrustee,
      isExecutor,
      isAdministrator,
      hasLegalAuthority,
      legalRole,
      relationshipToDecedent: data?.relationship_to_decedent ?? null,
      isLoading: enabled && isPending,
      isFetching,
    };
  }, [data, enabled, isPending, isFetching]);
}
